import type { OsmPlace, OsmPlaceKind } from '@roadworld/shared';

/** Maximum distance to consider a player "inside" a place node. Beyond this radius the
 *  nearest place is ignored (returns null) — prevents firing a notification for a
 *  neighbourhood node 5 km away just because it's loaded. */
const MAX_DISTANCE_M = 800;

/** Hysteresis: when transitioning between two places, the new candidate must be at
 *  least this much closer than the previously-fired place before we switch. Keeps the
 *  toast from flickering at the border between two equidistant neighbourhoods. */
const HYSTERESIS_DELTA_M = 120;

/** Earth radius in meters (WGS-84 mean). */
const EARTH_RADIUS_M = 6_378_137;

/** Tracks all `place=*` point features the client has seen and offers nearest-neighbour
 *  lookup with hysteresis for area-entry detection. Dedup is by OSM node id. */
export class PlaceIndex {
  private byId = new Map<number, OsmPlace>();
  /** Last place id we reported via {@link nearestWithHysteresis} — used as the anchor
   *  for the hysteresis check. Reset by {@link clear} on session change. */
  private currentId: number | null = null;
  private currentDistM = Infinity;

  addPlaces(places: readonly OsmPlace[]): void {
    for (const p of places) {
      if (!this.byId.has(p.id)) this.byId.set(p.id, p);
    }
  }

  clear(): void {
    this.byId.clear();
    this.currentId = null;
    this.currentDistM = Infinity;
  }

  size(): number {
    return this.byId.size;
  }

  /** Find the nearest place within MAX_DISTANCE_M, applying hysteresis: stays on the
   *  previously-returned place until another one is decisively closer. Returns null
   *  if no place is within range. */
  nearestWithHysteresis(
    lat: number,
    lng: number,
    kindFilter?: (kind: OsmPlaceKind) => boolean,
  ): OsmPlace | null {
    let best: OsmPlace | null = null;
    let bestDist = Infinity;
    let currentDistNow = Infinity;
    for (const p of this.byId.values()) {
      if (kindFilter && !kindFilter(p.kind)) continue;
      const d = haversineMeters(lat, lng, p.lat, p.lng);
      if (p.id === this.currentId) currentDistNow = d;
      if (d < bestDist) {
        best = p;
        bestDist = d;
      }
    }

    if (!best || bestDist > MAX_DISTANCE_M) {
      // Out of range of any known place — drop the sticky reference so re-entry
      // counts as a new event.
      this.currentId = null;
      this.currentDistM = Infinity;
      return null;
    }

    // Sticky if the current place is still in range AND the new candidate isn't
    // decisively closer.
    if (
      this.currentId !== null &&
      currentDistNow <= MAX_DISTANCE_M &&
      best.id !== this.currentId &&
      currentDistNow - bestDist < HYSTERESIS_DELTA_M
    ) {
      // Return the currently-locked place rather than the marginally-closer new one.
      const current = this.byId.get(this.currentId);
      if (current) return current;
    }

    this.currentId = best.id;
    this.currentDistM = bestDist;
    return best;
  }
}

/** Great-circle distance in meters between two lat/lng points. */
function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = Math.PI / 180;
  const dLat = (lat2 - lat1) * toRad;
  const dLng = (lng2 - lng1) * toRad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
